import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FacilityDetailsModal = ({ facility, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-foreground">{facility?.name}</h2>
              {facility?.emergency && (
                <span className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm font-medium rounded">
                  24/7 Emergency
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Icon name="MapPin" size={16} />
                <span>{facility?.country}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Building2" size={16} />
                <span>{facility?.type} Facility</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Star" size={16} className="fill-yellow-400 text-yellow-400" />
                <span className="font-medium">{facility?.rating}/5.0</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Google Maps Section */}
          {facility?.googleMapsUrl && (
            <div className="bg-muted/30 rounded-lg overflow-hidden">
              <div className="relative w-full h-[300px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3843.7234567891234!2d-60.9537!3d14.0756!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTTCsDA0JzMyLjIiTiA2MMKwNTcnMDYuNSJX!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full h-full"
                />
              </div>
            </div>
          )}

          {/* Contact Information */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Phone" size={18} />
              Contact Information
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <Icon name="MapPin" size={16} className="mt-0.5 text-muted-foreground" />
                <div>
                  <p className="font-medium text-foreground">Address</p>
                  <p className="text-muted-foreground">{facility?.address}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Phone" size={16} className="text-muted-foreground" />
                <div>
                  <p className="font-medium text-foreground">Phone</p>
                  <a href={`tel:${facility?.phone}`} className="text-accent hover:underline">
                    {facility?.phone}
                  </a>
                </div>
              </div>
              {facility?.whatsapp && (
                <div className="flex items-center gap-2">
                  <Icon name="MessageCircle" size={16} className="text-green-600 dark:text-green-400" />
                  <div>
                    <p className="font-medium text-foreground">WhatsApp</p>
                    <a 
                      href={`https://wa.me/${facility?.whatsapp?.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 dark:text-green-400 hover:underline"
                    >
                      {facility?.whatsapp}
                    </a>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={16} className="text-muted-foreground" />
                <div>
                  <p className="font-medium text-foreground">Operating Hours</p>
                  <p className="text-muted-foreground">{facility?.operatingHours}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Imaging Modalities */}
          <div>
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Activity" size={18} />
              Available Imaging Modalities
            </h3>
            <div className="flex flex-wrap gap-2">
              {facility?.modalities?.map(modality => (
                <span
                  key={modality}
                  className="px-3 py-2 bg-accent/10 text-accent font-medium rounded-lg"
                >
                  {modality}
                </span>
              ))}
            </div>
          </div>

          {/* Specialties */}
          <div>
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Stethoscope" size={18} />
              Specialties
            </h3>
            <div className="flex flex-wrap gap-2">
              {facility?.specialties?.map(specialty => (
                <span
                  key={specialty}
                  className="px-3 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg text-sm"
                >
                  {specialty}
                </span>
              ))}
            </div>
          </div>

          {/* Insurance Providers */}
          <div>
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Shield" size={18} />
              Accepted Insurance Providers
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {facility?.insurance?.map(insurance => (
                <div
                  key={insurance}
                  className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
                >
                  <Icon name="CheckCircle2" size={16} className="text-green-600 dark:text-green-400" />
                  <span className="text-sm text-green-700 dark:text-green-300">{insurance}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Wait Times */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Calendar" size={18} className="text-yellow-600 dark:text-yellow-400" />
              <h3 className="font-semibold text-yellow-900 dark:text-yellow-100">Wait Times</h3>
            </div>
            <p className="text-sm text-yellow-800 dark:text-yellow-200">{facility?.waitTime}</p>
          </div>

          {/* Accreditation */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Award" size={18} className="text-purple-600 dark:text-purple-400" />
              <h3 className="font-semibold text-purple-900 dark:text-purple-100">Accreditation</h3>
            </div>
            <p className="text-sm text-purple-800 dark:text-purple-200">{facility?.accreditation}</p>
          </div>

          {/* Medical Tourism Info */}
          {facility?.medicalTourism && (
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Plane" size={18} className="text-blue-600 dark:text-blue-400" />
                <h3 className="font-semibold text-blue-900 dark:text-blue-100">Medical Tourism Information</h3>
              </div>
              <div className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                <p>This facility offers comprehensive medical tourism packages for international patients.</p>
                {facility?.pricing && (
                  <p className="font-medium">{facility?.pricing}</p>
                )}
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Coordination of travel and accommodation</li>
                  <li>Multilingual staff available</li>
                  <li>International insurance accepted</li>
                  <li>Expedited appointment scheduling</li>
                </ul>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              onClick={() => window.open(`tel:${facility?.phone}`)}
              className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              <Icon name="Phone" size={18} />
              <span>Call Facility</span>
            </Button>
            {facility?.whatsapp && (
              <Button
                onClick={() => window.open(`https://wa.me/${facility?.whatsapp?.replace(/[^0-9]/g, '')}`)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                <Icon name="MessageCircle" size={18} />
                <span>WhatsApp</span>
              </Button>
            )}
            <Button
              onClick={() => window.open(facility?.googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(facility?.address)}`)}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Icon name="MapPin" size={18} />
              <span>Directions</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilityDetailsModal;